<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-rapportRelance-card">
    <div class="card-header">
      <h3 class="text-black fw-semibold">Rédiger un rapport de relance</h3>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="rapportRelanceForm" @submit="addRapportRelance" :validation-schema="rapportRelanceSchema">
        <div class="row">
          <!-- Date du règlement (en lecture seule) -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date de relance <span class="text-danger">*</span>
              </label>
              <Field name="dateRelance" type="datetime-local" :value="getCurrentDateTime()" readonly
                class="form-control shadow-none fs-md-15 text-black" />
              <ErrorMessage name="dateRelance" class="text-danger" />
            </div>
          </div>

          <!-- Sélection de la créance -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black mb-10">
                Créance <span class="text-danger">*</span>
              </label>
              <Field name="creance" v-model="creance" type="text" v-slot="{ field }">
                <Multiselect v-model="field.value" v-bind="field" :options="creanceOptions" :preserve-search="true"
                  :multiple="false" :searchable="true" placeholder="Sélectionner une créance" label="label"
                  track-by="label" @select="onCreanceSelect" />
              </Field>
              <ErrorMessage name="creance" class="text-danger" />
            </div>
          </div>

          <!-- Sélection de l'échéance -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black mb-10">
                Échéance <span class="text-danger">*</span>
              </label>
              <Field name="echeance" v-model="echeance" type="text" v-slot="{ field }">
                <Multiselect v-model="field.value" v-bind="field" :options="filteredEcheanceOptions"
                  :preserve-search="true" :multiple="false" :searchable="true" placeholder="Sélectionner une échéance"
                  label="label" track-by="label" @select="onEcheanceSelect" />
              </Field>
              <ErrorMessage name="echeance" class="text-danger" />
            </div>
          </div>
          <div class="col-md-8">
            <div class="form-group mb-3 mb-sm-3 mb-md-3">
              <label class="d-block text-black fw-semibold mb-1">
                Description <span class="text-danger">*</span>
              </label>
              <Field name="description" as="textarea" cols="15" rows="1" placeholder="Entrer la description"
                v-slot="{ field }" class="form-control shadow-none rounded-0 text-black">
                <textarea class="form-control shadow-none rounded-0 text-black" v-model="field.value"></textarea>
              </Field>
              <ErrorMessage name="description" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4">
  <div class="form-group mb-15 mb-sm-20 mb-md-25">
    <label class="d-block text-black fw-semibold mb-10">
      Canal Utilisé<span class="text-danger">*</span>
    </label>
    <Field name="canalUtilise" v-slot="{ field, value }">
      <Multiselect
        v-model="field.value"
        v-bind="field"
        :options="['SMS', 'Appel téléphonique', 'Mail', 'Visite', 'SMS Whatsapp', 'Appel Whatsapp']"
        :searchable="true"
        placeholder="Sélectionner le canal"
        :multiple="false"
       
      />
    </Field>
    <ErrorMessage name="canalUtilise" class="text-danger" />
  </div>
</div>
          <!-- Boutons -->
          <div class="col-md-12 mt-3">
            <div class="d-flex align-items-center">
              <button ref="submitButton" class="btn btn-primary me-3" type="submit">
                Enregistrer le rapport
              </button>
              <router-link to="/rapportRelances/liste-rapportRelance" class="btn btn-danger">
                <i class="fa fa-trash-o lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative"></span>Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, watch } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { error, format_date, success } from '@/utils/utils';
import Multiselect from '@vueform/multiselect/src/Multiselect';
import router from '@/router';

export default defineComponent({
  name: "AddRapportRelance",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,

  },
  setup: () => {
    const rapportRelanceSchema = Yup.object().shape({
      dateRelance: Yup.string().required('La date du règlement est obligatoire'),
      creance: Yup.number().required('La créance est obligatoire').typeError('Veuillez sélectionner une créance'),
      echeance: Yup.number().required('L\'échéance est obligatoire').typeError('Veuillez sélectionner une échéance'),
      canalUtilise: Yup.string().required('Le canal utilisé est obligatoire'),
      // // Modifiez ceci pour accepter un tableau
      // canalUtilise: Yup.array()
      //   .of(Yup.string())
      //   .min(1, 'Veuillez sélectionner au moins un canal')
      //   .required('Le canal utilisé est obligatoire'),
      description: Yup.string().required('La description est obligatoire'),
    });

    
    // Fonction pour obtenir la date et l'heure actuelles
    function getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Fonction pour formater la date et l'heure
    const format_date_time = (date: string | Date): string => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}h${minutes}`;
    };

    // Variables réactives
    const creanceOptions = ref([]);
    const echeanceOptions = ref([]);

    const submitButton = ref<HTMLButtonElement | null>(null);
    const creance = ref(null);
    const echeance = ref(null);
    const selectedEcheance = ref<any>(null);
    // const canalUtilise = ref<string[]>([]); // Typage explicite comme tableau de strings
    // const canalOptions = ref([]);



    // Charger les créances
    const getAllCreances = async () => {
      try {
        const response = await ApiService.get('/all/creances');
        const creancesData = response.data.data;
        creanceOptions.value = creancesData.map((cre: any) => ({
          value: cre.id,
          label: `${cre.refCrean} - ${cre.client.nomClient && cre.client.prenomClient
            ? `${cre.client.nomClient} ${cre.client.prenomClient}`
            : cre.client.nomClient || cre.client.raisonSociale || 'Client inconnu'
            } - Montant: ${cre.montant}`,
          creance: cre,
        }));
      } catch (err) {
        error(err.message);
      }
    };

    // Charger les échéances
    const getAllEcheances = async () => {
      try {
        const response = await ApiService.get('/all/echeances');
        const echeancesData = response.data.data;
        echeanceOptions.value = echeancesData.map((ech: any) => ({
          value: ech.id,
          label: `${format_date_time(ech.date)} - ${ech.creance.client.nomClient && ech.creance.client.prenomClient
            ? `${ech.creance.client.nomClient} ${ech.creance.client.prenomClient}`
            : ech.creance.client.nomClient || ech.creance.client.raisonSociale || 'Client inconnu'
            } - ${ech.montant} (Reste: ${ech.reste_a_payer})`,
          echeance: ech,
        }));
      } catch (err) {
        error(err.message);
      }
    };

    // Filtrer les échéances en fonction de la créance sélectionnée
    const filteredEcheanceOptions = computed(() => {
      if (!creance.value) return [];
      return echeanceOptions.value.filter((option: any) => option.echeance.creance.id === creance.value);
    });


    // Mettre à jour les informations lors de la sélection de la créance
    const onCreanceSelect = (value: number) => {
      echeance.value = null;
      selectedEcheance.value = null;
      resetFields();
    };

    // Mettre à jour les informations lors de la sélection de l'échéance
    const onEcheanceSelect = (value: number) => {
      const selected = echeanceOptions.value.find((option: any) => option.value === value);
      selectedEcheance.value = selected ? selected.echeance : null;

    };

    const resetFields = () => {
    };


    // Soumettre le rapport
    const addRapportRelance = async (values: any, { resetForm }: any) => {
      if (submitButton.value) {
        submitButton.value.disabled = true;
      }

      const rapportRelanceData: any = {
        dateRelance: values.dateRelance,
        echeance: values.echeance,
        creance: values.creance,
        description: values.description,
        canalUtilise: values.canalUtilise,

      };
      try {
        const response = await ApiService.post("/rapport-relance", rapportRelanceData);
        if (response.data.code === 201) {
          success(response.data.message);
          resetForm();
          router.push({ name: "ListeRapportRelance" });
        }
      } catch (err) {
        submitButton.value!.disabled = false;
        error(err.message);
      }
    };

    // Charger les données au montage
    onMounted(() => {
      getAllCreances();
      getAllEcheances();

    });

    return {
      rapportRelanceSchema,
      addRapportRelance,
      getCurrentDateTime,
      format_date,
      format_date_time,
      creanceOptions,
      echeanceOptions,
      filteredEcheanceOptions,
      submitButton,
      creance,
      echeance,
      selectedEcheance,
      onCreanceSelect,
      //canalUtilise,
      onEcheanceSelect,

    };
  },
});
</script>

<style scoped>
.error {
  color: red;
}
</style>